/**
 * Interface defining the structure of purchase event data for tracking
 * Used to ensure consistent data format when sending purchase events to Meta Pixel
 */
interface PurchaseEventData {
  transaction_id: string; // Unique identifier for the transaction
  value: number; // Total monetary value of the transaction
  currency: string; // Currency code (e.g., "INR", "USD")
  items: Array<{
    // Array of items purchased in this transaction
    item_id: string; // Unique identifier for the item
    item_name: string; // Display name of the item
    price: number; // Price per unit of the item
    quantity: number; // Number of units purchased
    item_category?: string; // Optional primary category of the item
    item_category2?: string; // Optional secondary category of the item
  }>;
}

// Define pixel IDs and their corresponding tracking functions
export const PIXEL_IDS = {
  MAIN: "1733058363584523",
  ZO_TRIP: "931424305781506",
} as const;

const isProduction = process.env.REACT_APP_ENV === "production";

/**
 * Tracks events using Meta Pixel (Facebook Pixel)
 * @param eventName - Name of the event to track
 * @param data - Additional data to send with the event
 * @param pixelId - Optional pixel ID to track the event to. If not provided, tracks to main pixel only
 *
 * Note: For standard events like "Purchase", we use fbq("trackSingle")
 * For custom events, we use fbq("trackSingleCustom")
 */
export const trackMetaPixelEvent = (
  eventName: string,
  data?: any,
  pixelId?: string
) => {
  if (!isProduction) {
    return; // Only proceed if in production environment
  }
  
  try {
    // Check if we're in a browser environment
    if (typeof window !== "undefined") {
      const trackEvent = (pid: string) => {
        // Get the fbq function
        const fbq = (window as any).fbq;

        if (!fbq) {
          console.error("Meta Pixel fbq function not found");
          return;
        }

        if (
          eventName === "Purchase" ||
          eventName === "AddToCart" ||
          eventName === "InitiateCheckout" ||
          eventName === "ViewContent" ||
          eventName === "PageView"
        ) {
          // Standard event - use trackSingle
          console.log("trackSingle", pid, eventName, data);
          fbq("trackSingle", pid, eventName, data);
        } else {
          // Custom event - use trackSingleCustom
          console.log("trackSingleCustom", pid, eventName, { value: data });
          fbq("trackSingleCustom", pid, eventName, { value: data });
        }
      };

      // If pixelId is provided, track to that pixel
      if (pixelId) {
        trackEvent(pixelId);
      } else {
        // Track to main pixel by default (maintaining backward compatibility)
        trackEvent(PIXEL_IDS.MAIN);
      }
    }
  } catch (error) {
    console.error("Meta Pixel tracking error:", error);
  }
};

/**
 * Convenience function to track purchase events
 * @param data - Purchase event data including transaction details and items
 * @param pixelId - Optional pixel ID to track the event to. If not provided, tracks to main pixel only
 *
 * Sets default currency to INR if not provided
 */
export const trackPurchaseEvent = (
  data: PurchaseEventData,
  pixelId?: string
) => {
  if (!isProduction) {
    return; // Only proceed if in production environment
  }
  
  trackMetaPixelEvent(
    "Purchase",
    {
      ...data,
      currency: data.currency || "INR", // Default to INR if currency not specified
    },
    pixelId
  );
};

/**
 * Convenience function to track ZO Trip specific events
 * @param eventName - Name of the event to track
 * @param data - Additional data to send with the event
 */
export const trackZoTripEvent = (eventName: string, data?: any) => {
  if (!isProduction) {
    return; // Only proceed if in production environment
  }
  
  trackMetaPixelEvent(eventName, data, PIXEL_IDS.ZO_TRIP);
};

export const trackZostelEvent = (eventName: string, data?: any) => {
  if (!isProduction) {
    return; // Only proceed if in production environment
  }
  
  trackMetaPixelEvent(eventName, data, PIXEL_IDS.MAIN);
};
